import React, { useState, useEffect } from 'react';
import './InputTestResults.css';

function InputTestResults({ apiBase, roleColor }) {
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [formData, setFormData] = useState({
    score: '',
    status: 'passed',
    reason: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadApplicants();
  }, []);

  const loadApplicants = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiBase}/admissions/applicants?limit=100`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (data.ok && data.data) {
        setApplicants(data.data.applicants || []);
      }
    } catch (err) {
      console.error('Error loading applicants:', err);
      setMessage('Gagal memuat data pendaftar');
    } finally {
      setLoading(false);
    }
  };

  const filteredApplicants = applicants.filter(app =>
    app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectApplicant = (applicant) => {
    setSelectedApplicant(applicant);
    setFormData({
      score: '',
      status: 'passed',
      reason: ''
    });
    setMessage('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleStatusChange = (e) => {
    setFormData(prev => ({
      ...prev,
      status: e.target.value,
      reason: e.target.value === 'passed' ? '' : prev.reason
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedApplicant) {
      setMessage('Pilih pendaftar terlebih dahulu');
      return;
    }
    if (!formData.score || formData.score < 0 || formData.score > 100) {
      setMessage('Nilai harus antara 0-100');
      return;
    }
    if (formData.status === 'failed' && !formData.reason.trim()) {
      setMessage('Keterangan diperlukan jika tidak lulus');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(
        `${apiBase}/admissions/applicants/${selectedApplicant.id}/test-results`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            score: parseInt(formData.score),
            status: formData.status,
            reason: formData.reason || null
          })
        }
      );
      const data = await response.json();
      if (data.ok) {
        setMessage('Hasil test berhasil disimpan');
        setSelectedApplicant(null);
        setFormData({ score: '', status: 'passed', reason: '' });
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(data.message || 'Gagal menyimpan hasil test');
      }
    } catch (err) {
      console.error('Error submitting:', err);
      setMessage('Terjadi kesalahan saat menyimpan');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="input-test-results">
      <div className="form-container">
        <div className="form-section">
          <h2>Input Hasil Test</h2>
          <p className="form-description">Masukkan hasil test untuk pendaftar</p>

          <div className="applicants-selector">
            <h3>Pilih Pendaftar</h3>
            <input
              type="text"
              placeholder="Cari nama atau email pendaftar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />

            {loading ? (
              <div className="loading-state">
                <svg className="spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="12" cy="12" r="10" />
                </svg>
                <p>Memuat data pendaftar...</p>
              </div>
            ) : (
              <div className="applicants-list">
                {filteredApplicants.length === 0 ? (
                  <div className="empty-state">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                    </svg>
                    <p>Tidak ada pendaftar</p>
                  </div>
                ) : (
                  filteredApplicants.map(app => (
                    <div
                      key={app.id}
                      className={`applicant-item ${selectedApplicant?.id === app.id ? 'selected' : ''}`}
                      onClick={() => handleSelectApplicant(app)}
                    >
                      <div className="applicant-info">
                        <p className="applicant-name">{app.name}</p>
                        <p className="applicant-email">{app.email}</p>
                        <p className="applicant-level">{app.level || 'Tidak diketahui'}</p>
                      </div>
                      {selectedApplicant?.id === app.id && (
                        <svg className="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <div className="form-section">
          {selectedApplicant ? (
            <>
              <h3>Data Pendaftar</h3>
              <div className="selected-info">
                <div className="info-item">
                  <span className="info-label">Nama</span>
                  <span className="info-value">{selectedApplicant.name}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Email</span>
                  <span className="info-value">{selectedApplicant.email}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Jenjang</span>
                  <span className="info-value">{selectedApplicant.level || '-'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Lokasi</span>
                  <span className="info-value">{selectedApplicant.location || '-'}</span>
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="score">
                    Nilai Test (0-100)
                    <span className="required">*</span>
                  </label>
                  <input
                    id="score"
                    type="number"
                    name="score"
                    min="0"
                    max="100"
                    value={formData.score}
                    onChange={handleInputChange}
                    placeholder="Masukkan nilai..."
                    required
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>
                    Status
                    <span className="required">*</span>
                  </label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="status"
                        value="passed"
                        checked={formData.status === 'passed'}
                        onChange={handleStatusChange}
                      />
                      <span>Lulus</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="status"
                        value="failed"
                        checked={formData.status === 'failed'}
                        onChange={handleStatusChange}
                      />
                      <span>Tidak Lulus</span>
                    </label>
                  </div>
                </div>

                {formData.status === 'failed' && (
                  <div className="form-group">
                    <label htmlFor="reason">
                      Keterangan Tidak Lulus
                      <span className="required">*</span>
                    </label>
                    <textarea
                      id="reason"
                      name="reason"
                      value={formData.reason}
                      onChange={handleInputChange}
                      placeholder="Jelaskan alasan tidak lulus..."
                      rows="4"
                      required
                      className="form-textarea"
                    />
                  </div>
                )}

                {message && (
                  <div className={`message ${message.includes('berhasil') ? 'success' : 'error'}`}>
                    {message}
                  </div>
                )}

                <div className="form-actions">
                  <button
                    type="submit"
                    disabled={submitting}
                    style={{ background: roleColor }}
                    className="btn-submit"
                  >
                    {submitting ? 'Menyimpan...' : 'Simpan Hasil Test'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedApplicant(null);
                      setFormData({ score: '', status: 'passed', reason: '' });
                      setMessage('');
                    }}
                    className="btn-cancel"
                  >
                    Batal
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="empty-form">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              <p>Pilih pendaftar untuk memasukkan hasil test</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default InputTestResults;
